import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  GLOBAL_LOBBY_CHAT_KEY,
  GLOBAL_LOBBY_DM_CHAT_KEY,
  GLOBAL_LOBBY_FRIEND_REQUESTS_KEY,
  DEFAULT_GLOBAL_LOBBY_MESSAGES,
  DEFAULT_DM_LOBBY_MESSAGES,
  parseLobbyMessagesFromStorage,
  parseDmMessagesFromStorage,
  normalizeIncomingDmServerRow,
  sortChatRowsByCreatedAtThenId,
  mergeFriendRequestsLocalAndServer,
  acceptedFriendsFromRequests,
  getInitialMailFriendRequests,
  senderFromCurrentUserLobby,
  filterOutAcceptedFriendWithPeer,
} from './socialLobbyModel';

const FRIEND_REQUEST_RESEND_COOLDOWN_MS = 120_000;
const FRIEND_PRESENCE_POLL_MS = 25_000;
const MAX_GLOBAL_CHAT = 80;
const MAX_DM = 300;

/**
 * Hub social SSOT: friend requests, global + DM chat buffers, Colyseus listener wiring.
 * OnlineLobby still forwards friend events into handleLobbyFriendRequest* from App (same handlers).
 */
export function useSocialLobby({
  apiBaseUrl,
  lobbyUser,
  sessionToken,
  socket,
  setError,
  setSuccess,
  dmSelectedPeerId,
  hubChatChannel,
  getDevDeviceId,
  devUserProfile,
}) {
  const [mailFriendRequests, setMailFriendRequests] = useState(() =>
    getInitialMailFriendRequests({ getDevDeviceId, devUserProfile })
  );
  const [globalLobbyMessages, setGlobalLobbyMessages] = useState(() =>
    typeof window === 'undefined'
      ? DEFAULT_GLOBAL_LOBBY_MESSAGES
      : parseLobbyMessagesFromStorage(localStorage.getItem(GLOBAL_LOBBY_CHAT_KEY), DEFAULT_GLOBAL_LOBBY_MESSAGES)
  );
  const [dmLobbyMessages, setDmLobbyMessages] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_DM_LOBBY_MESSAGES;
    if (localStorage.getItem('token')) return [];
    return parseDmMessagesFromStorage(localStorage.getItem(GLOBAL_LOBBY_DM_CHAT_KEY), DEFAULT_DM_LOBBY_MESSAGES);
  });
  const [dmUnreadByPeer, setDmUnreadByPeer] = useState({});
  const [friendPresenceByUserId, setFriendPresenceByUserId] = useState({});

  const hubDmFriendsList = useMemo(
    () => acceptedFriendsFromRequests(mailFriendRequests, lobbyUser),
    [mailFriendRequests, lobbyUser]
  );

  useEffect(() => {
    if (sessionToken) return;
    try {
      localStorage.setItem(GLOBAL_LOBBY_FRIEND_REQUESTS_KEY, JSON.stringify(mailFriendRequests.slice(-120)));
    } catch {
      /* ignore */
    }
  }, [mailFriendRequests, sessionToken]);

  useEffect(() => {
    try {
      localStorage.setItem(GLOBAL_LOBBY_CHAT_KEY, JSON.stringify(globalLobbyMessages.slice(-80)));
    } catch {
      /* ignore */
    }
  }, [globalLobbyMessages]);

  useEffect(() => {
    if (sessionToken) return;
    try {
      localStorage.setItem(GLOBAL_LOBBY_DM_CHAT_KEY, JSON.stringify(dmLobbyMessages.slice(-80)));
    } catch {
      /* ignore */
    }
  }, [dmLobbyMessages, sessionToken]);

  const refetchFriendRequestsFromApi = useCallback(() => {
    const token = sessionToken;
    const uid = String(lobbyUser?.id || '').trim();
    if (!token || !uid) return;
    (async () => {
      try {
        const r = await fetch(`${apiBaseUrl}/api/friends/requests`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const data = await r.json();
        const list = Array.isArray(data.requests) ? data.requests : [];
        setMailFriendRequests((prev) => mergeFriendRequestsLocalAndServer(prev, list, uid));
      } catch {
        /* ignore */
      }
    })();
  }, [apiBaseUrl, sessionToken, lobbyUser?.id]);

  const refetchFriendPresence = useCallback(() => {
    const token = sessionToken;
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${apiBaseUrl}/api/friends/presence`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const data = await r.json();
        const map = data?.byUserId && typeof data.byUserId === 'object' ? data.byUserId : {};
        setFriendPresenceByUserId(map);
      } catch {
        /* ignore */
      }
    })();
  }, [apiBaseUrl, sessionToken]);

  useEffect(() => {
    if (!sessionToken) {
      setFriendPresenceByUserId({});
      return undefined;
    }
    refetchFriendPresence();
    const id = setInterval(refetchFriendPresence, FRIEND_PRESENCE_POLL_MS);
    const onVis = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        refetchFriendPresence();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVis);
    }
    return () => {
      clearInterval(id);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVis);
      }
    };
  }, [sessionToken, refetchFriendPresence]);

  useEffect(() => {
    if (!sessionToken || !lobbyUser?.id) return undefined;
    let cancelled = false;
    const uid = String(lobbyUser.id);
    (async () => {
      try {
        const r = await fetch(`${apiBaseUrl}/api/friends/requests`, {
          headers: { Authorization: `Bearer ${sessionToken}` },
        });
        if (!r.ok || cancelled) return;
        const data = await r.json();
        const list = Array.isArray(data.requests) ? data.requests : [];
        if (!cancelled) setMailFriendRequests((prev) => mergeFriendRequestsLocalAndServer(prev, list, uid));
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionToken, lobbyUser?.id, apiBaseUrl]);

  const ingestGlobalLobbyChatServerRow = useCallback((raw) => {
    if (!raw || typeof raw !== 'object') return;
    const id = String(raw.id || '').trim();
    if (!id) return;
    const s = raw.sender;
    if (!s || typeof s !== 'object') return;
    const sender = {
      id: String(s.id || 'player'),
      username: String(s.username || 'Игрок').trim() || 'Игрок',
      clan: String(s.clan || 'Без клана'),
      level: Math.max(1, Math.floor(Number(s.level) || 1)),
      avatarUrl: s.avatarUrl != null ? String(s.avatarUrl).trim() || null : null,
    };
    const text = String(raw.text || '').trim();
    if (!text) return;
    const createdAt = Math.floor(Number(raw.createdAt) || Date.now());
    setGlobalLobbyMessages((prev) => {
      if (prev.some((m) => m.id === id)) return sortChatRowsByCreatedAtThenId(prev).slice(-MAX_GLOBAL_CHAT);
      return sortChatRowsByCreatedAtThenId([...prev, { id, sender, text, createdAt }]).slice(-MAX_GLOBAL_CHAT);
    });
  }, []);

  const ingestFriendDmServerRow = useCallback(
    (payload) => {
      const row = normalizeIncomingDmServerRow(payload);
      if (!row) return;
      setDmLobbyMessages((prev) => {
        if (prev.some((m) => m.id === row.id)) return sortChatRowsByCreatedAtThenId(prev).slice(-MAX_DM);
        return sortChatRowsByCreatedAtThenId([...prev, row]).slice(-MAX_DM);
      });
      if (!row.outgoing) {
        const currentPeer = String(dmSelectedPeerId || '').trim();
        if (!currentPeer || currentPeer !== row.peerId || hubChatChannel !== 'dm') {
          setDmUnreadByPeer((prev) => ({
            ...prev,
            [row.peerId]: Math.max(0, Number(prev[row.peerId]) || 0) + 1,
          }));
        }
      }
    },
    [dmSelectedPeerId, hubChatChannel]
  );

  const appendGlobalLobbyMessageLocal = useCallback((senderLike, text) => {
    const safeText = String(text || '').trim();
    if (!safeText) return;
    const sender =
      typeof senderLike === 'string'
        ? {
            id: senderLike.toLowerCase().replace(/\s+/g, '-'),
            username: senderLike,
            clan: senderLike === 'Система' ? 'SYSTEM' : 'Без клана',
            level: senderLike === 'Система' ? 99 : 1,
            avatarUrl: null,
          }
        : {
            id: senderLike?.id || 'player',
            username: senderLike?.username || 'Игрок',
            clan: senderLike?.clan || 'Без клана',
            level: Number(senderLike?.level) || 1,
            avatarUrl: senderLike?.avatarUrl || null,
          };
    const row = {
      id: `g-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      sender,
      text: safeText,
      createdAt: Date.now(),
    };
    setGlobalLobbyMessages((prev) => sortChatRowsByCreatedAtThenId([...prev, row]).slice(-MAX_GLOBAL_CHAT));
  }, []);

  const handleLobbyFriendRequestDelivery = useCallback(
    (payload) => {
      const recipientUserId = String(payload?.recipientUserId || '');
      const myId = String(lobbyUser?.id || '');
      if (!recipientUserId || !myId || recipientUserId !== myId) return;
      const rawFrom = payload?.from;
      if (!rawFrom || typeof rawFrom !== 'object') return;
      const fromId = String(rawFrom.id || '').trim();
      if (!fromId || fromId === myId || fromId === 'system') return;
      const from = {
        id: fromId,
        username: String(rawFrom.username || 'Игрок').trim() || 'Игрок',
        clan: String(rawFrom.clan || 'Без клана'),
        level: Math.max(1, Math.floor(Number(rawFrom.level) || 1)),
        avatarUrl: rawFrom.avatarUrl ? String(rawFrom.avatarUrl).trim() : null,
      };
      const createdAt = Math.floor(Number(payload.createdAt) || Date.now());
      const srvId = payload?.requestId ? String(payload.requestId) : '';
      const to = senderFromCurrentUserLobby(lobbyUser);
      setMailFriendRequests((prev) => {
        const friendsWith = prev.some(
          (r) =>
            r.status === 'accepted' &&
            ((String(r.from?.id) === myId && String(r.to?.id) === fromId) ||
              (String(r.from?.id) === fromId && String(r.to?.id) === myId))
        );
        if (friendsWith) return prev;
        const existsPending = prev.find(
          (r) =>
            r.status === 'pending' &&
            r.type === 'incoming' &&
            String(r.from?.id) === fromId &&
            String(r.to?.id) === myId
        );
        if (existsPending) {
          return prev.map((r) =>
            r.id === existsPending.id ? { ...r, id: srvId || r.id, from, seen: false, createdAt } : r
          );
        }
        const id = srvId || `fr-${createdAt}-${Math.random().toString(36).slice(2, 7)}`;
        return [
          {
            id,
            from,
            to,
            status: 'pending',
            type: 'incoming',
            seen: false,
            createdAt,
          },
          ...prev,
        ];
      });
    },
    [lobbyUser]
  );

  const handleLobbyFriendRequestResolved = useCallback(
    (payload) => {
      const recipientUserId = String(payload?.recipientUserId || '');
      const myId = String(lobbyUser?.id || '');
      if (!recipientUserId || !myId || recipientUserId !== myId) return;
      const status = String(payload?.status || '').toLowerCase();
      if (status !== 'accepted' && status !== 'declined') return;
      const rawResolver = payload?.resolver;
      if (!rawResolver || typeof rawResolver !== 'object') return;
      const resolverId = String(rawResolver.id || '').trim();
      if (!resolverId || resolverId === myId) return;
      const resolver = {
        id: resolverId,
        username: String(rawResolver.username || 'Игрок').trim() || 'Игрок',
        clan: String(rawResolver.clan || 'Без клана'),
        level: Math.max(1, Math.floor(Number(rawResolver.level) || 1)),
        avatarUrl: rawResolver.avatarUrl ? String(rawResolver.avatarUrl).trim() : null,
      };
      const createdAt = Math.floor(Number(payload.createdAt) || Date.now());
      const serverRid = payload.requestId ? String(payload.requestId).trim() : '';
      const from = senderFromCurrentUserLobby(lobbyUser);
      setMailFriendRequests((prev) => {
        let any = false;
        const mapped = prev.map((r) => {
          if (serverRid && String(r.id) === serverRid) {
            any = true;
            return {
              ...r,
              to: { ...(r.to || {}), ...resolver },
              status,
              seen: true,
            };
          }
          if (
            r.type === 'outgoing' &&
            r.status === 'pending' &&
            String(r.from?.id) === myId &&
            String(r.to?.id) === resolverId
          ) {
            any = true;
            return {
              ...r,
              to: { ...(r.to || {}), ...resolver },
              status,
              seen: true,
            };
          }
          return r;
        });
        if (any) return mapped;
        if (status === 'accepted') {
          return [
            {
              id: `fr-${createdAt}-${Math.random().toString(36).slice(2, 7)}`,
              from,
              to: resolver,
              status: 'accepted',
              type: 'outgoing',
              seen: true,
              createdAt,
            },
            ...prev,
          ];
        }
        return mapped;
      });
      if (status === 'accepted') setSuccess(`${resolver.username} принял(а) запрос закорефаниться`);
      else setSuccess(`${resolver.username} отклонил(а) заявку`);
    },
    [lobbyUser, setSuccess]
  );

  const sendFriendRequestToProfileUser = useCallback(
    async (target) => {
      if (!target || !lobbyUser?.id) return null;
      const myId = String(lobbyUser.id);
      const tid = String(target.id);
      if (tid === myId || tid === 'system') {
        setError('Нельзя отправить запрос этому пользователю.');
        return null;
      }

      const friendsWith = mailFriendRequests.some(
        (r) =>
          r.status === 'accepted' &&
          ((String(r.from?.id) === myId && String(r.to?.id) === tid) ||
            (String(r.from?.id) === tid && String(r.to?.id) === myId))
      );
      if (friendsWith) {
        setError('Вы уже в братве с этим игроком.');
        return null;
      }

      const incomingPending = mailFriendRequests.find(
        (r) =>
          r.status === 'pending' &&
          r.type === 'incoming' &&
          String(r.from?.id) === tid &&
          String(r.to?.id) === myId
      );
      if (incomingPending) {
        setError('Этот игрок уже предложил закорефаниться. Откройте «Братва», чтобы ответить.');
        return null;
      }

      const outgoingPending = mailFriendRequests.find(
        (r) =>
          r.status === 'pending' &&
          r.type === 'outgoing' &&
          String(r.from?.id) === myId &&
          String(r.to?.id) === tid
      );
      const now = Date.now();
      if (outgoingPending) {
        const elapsed = now - (Number(outgoingPending.createdAt) || 0);
        if (elapsed < FRIEND_REQUEST_RESEND_COOLDOWN_MS) {
          const leftSec = Math.ceil((FRIEND_REQUEST_RESEND_COOLDOWN_MS - elapsed) / 1000);
          setError(`Повторная заявка доступна через ${leftSec} сек.`);
          return null;
        }
      } else {
        const otherPending = mailFriendRequests.some(
          (r) =>
            r.status === 'pending' &&
            ((String(r.from?.id) === myId && String(r.to?.id) === tid) ||
              (String(r.from?.id) === tid && String(r.to?.id) === myId))
        );
        if (otherPending) {
          setError('Запрос между этими игроками уже ожидает ответа.');
          return null;
        }
      }

      const wasResend = Boolean(outgoingPending);

      if (sessionToken) {
        try {
          const resp = await fetch(`${apiBaseUrl}/api/friends/request`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${sessionToken}`,
            },
            body: JSON.stringify({ targetUserId: tid }),
          });
          const data = await resp.json().catch(() => ({}));
          if (!resp.ok) {
            const errText =
              typeof data.error === 'string' && data.error.trim() ? data.error.trim() : 'Не удалось отправить заявку';
            setError(errText);
            return null;
          }
          const dto = data.request;
          if (dto) {
            setMailFriendRequests((prev) => mergeFriendRequestsLocalAndServer(prev, [dto], myId));
          }
          setSuccess(
            wasResend || data.created === false
              ? `Заявка обновлена: ${target.username}`
              : `Запрос закорефаниться отправлен: ${target.username}`
          );
          try {
            if (socket?.emit && typeof socket.emit === 'function' && tid) {
              socket.emit('friend_request_notify', { targetUserId: tid });
            }
          } catch {
            /* ignore transport errors */
          }
          return wasResend ? 'resent' : 'sent';
        } catch {
          setError('Сеть: не удалось отправить заявку');
          return null;
        }
      }

      const from = senderFromCurrentUserLobby(lobbyUser);
      const to = {
        id: target.id,
        username: target.username,
        clan: target.clan || 'Без клана',
        level: Number(target.level) || 1,
        avatarUrl: target.avatarUrl || null,
      };
      const newRow = {
        id: `fr-${now}-${Math.random().toString(36).slice(2, 7)}`,
        from,
        to,
        status: 'pending',
        type: 'outgoing',
        seen: true,
        createdAt: now,
      };
      setMailFriendRequests((prev) => {
        const dropId = outgoingPending?.id;
        const base = dropId ? prev.filter((r) => r.id !== dropId) : prev;
        return [newRow, ...base];
      });
      setSuccess(
        wasResend
          ? `Запрос закорефаниться обновлен: ${target.username}`
          : `Запрос закорефаниться отправлен: ${target.username}`
      );
      const result = wasResend ? 'resent' : 'sent';
      if (result === 'sent' || result === 'resent') {
        try {
          if (socket?.emit && typeof socket.emit === 'function' && tid) {
            socket.emit('friend_request_notify', { targetUserId: tid });
          }
        } catch {
          /* ignore transport errors */
        }
      }
      return result;
    },
    [lobbyUser, mailFriendRequests, sessionToken, socket, setError, setSuccess, apiBaseUrl]
  );

  const emitFriendRequestResolvedToServer = useCallback(
    (otherUserId, status) => {
      const tid = String(otherUserId || '').trim();
      if (!tid || (status !== 'accepted' && status !== 'declined')) return;
      try {
        if (socket?.emit && typeof socket.emit === 'function') {
          socket.emit('friend_request_resolve', { otherUserId: tid, status });
          return;
        }
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('seka-friend-request-resolve', { detail: { otherUserId: tid, status } })
          );
        }
      } catch {
        /* ignore transport errors */
      }
    },
    [socket]
  );

  const removeAcceptedFriendByPeerId = useCallback(
    (peerId) => {
      const myId = String(lobbyUser?.id || '').trim();
      setMailFriendRequests((prev) => filterOutAcceptedFriendWithPeer(prev, peerId, myId));
    },
    [lobbyUser?.id]
  );

  const syncHubAfterReconnect = useCallback(() => {
    try {
      if (socket?.emit && typeof socket.emit === 'function') {
        socket.emit('friend_dm_sync');
      }
    } catch {
      /* ignore */
    }
    refetchFriendRequestsFromApi();
    refetchFriendPresence();
  }, [socket, refetchFriendRequestsFromApi, refetchFriendPresence]);

  useEffect(() => {
    if (!socket?.on) return undefined;
    const handler = (payload) => {
      handleLobbyFriendRequestDelivery(payload);
    };
    socket.on('lobby_friend_request', handler);
    return () => {
      socket.off('lobby_friend_request', handler);
    };
  }, [socket, handleLobbyFriendRequestDelivery]);

  useEffect(() => {
    if (!socket?.on) return undefined;
    const handler = (payload) => {
      handleLobbyFriendRequestResolved(payload);
    };
    socket.on('lobby_friend_request_resolved', handler);
    return () => {
      socket.off('lobby_friend_request_resolved', handler);
    };
  }, [socket, handleLobbyFriendRequestResolved]);

  useEffect(() => {
    if (!socket?.on) return undefined;
    const onRow = (payload) => {
      ingestGlobalLobbyChatServerRow(payload);
    };
    socket.on('lobby_global_lobby_chat', onRow);
    return () => {
      socket.off('lobby_global_lobby_chat', onRow);
    };
  }, [socket, ingestGlobalLobbyChatServerRow]);

  useEffect(() => {
    if (!socket?.on) return undefined;
    const onDm = (payload) => {
      ingestFriendDmServerRow(payload);
    };
    socket.on('lobby_friend_dm_message', onDm);
    return () => {
      socket.off('lobby_friend_dm_message', onDm);
    };
  }, [socket, ingestFriendDmServerRow]);

  useEffect(() => {
    if (!socket?.on) return undefined;
    const onDmSync = (payload) => {
      const rows = Array.isArray(payload?.messages) ? payload.messages : [];
      rows.forEach((row) => ingestFriendDmServerRow(row));
    };
    socket.on('lobby_friend_dm_sync', onDmSync);
    return () => {
      socket.off('lobby_friend_dm_sync', onDmSync);
    };
  }, [socket, ingestFriendDmServerRow]);

  useEffect(() => {
    if (!socket?.on) return undefined;
    const onReconnect = () => {
      syncHubAfterReconnect();
    };
    socket.on('reconnected', onReconnect);
    return () => {
      socket.off('reconnected', onReconnect);
    };
  }, [socket, syncHubAfterReconnect]);

  useEffect(() => {
    if (!socket?.emit || typeof socket.emit !== 'function') return;
    try {
      socket.emit('friend_dm_sync');
    } catch {
      /* ignore */
    }
  }, [socket]);

  useEffect(() => {
    const peerId = String(dmSelectedPeerId || '').trim();
    if (!peerId || !sessionToken || !hubDmFriendsList.some((f) => String(f.id) === peerId)) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${apiBaseUrl}/api/friends/dm/thread/${encodeURIComponent(peerId)}?limit=120`, {
          headers: { Authorization: `Bearer ${sessionToken}` },
        });
        if (!r.ok || cancelled) return;
        const data = await r.json();
        const rows = Array.isArray(data.messages) ? data.messages : [];
        const normalized = rows.map((x) => normalizeIncomingDmServerRow(x)).filter(Boolean);
        if (cancelled || !normalized.length) return;
        setDmLobbyMessages((prev) => {
          const ids = new Set(prev.map((m) => String(m.id)));
          const add = normalized.filter((m) => !ids.has(String(m.id)));
          if (!add.length) return prev;
          return sortChatRowsByCreatedAtThenId([...prev, ...add]).slice(-MAX_DM);
        });
      } catch {
        /* ignore thread preload errors */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dmSelectedPeerId, sessionToken, hubDmFriendsList, apiBaseUrl]);

  return {
    mailFriendRequests,
    setMailFriendRequests,
    globalLobbyMessages,
    setGlobalLobbyMessages,
    dmLobbyMessages,
    setDmLobbyMessages,
    dmUnreadByPeer,
    setDmUnreadByPeer,
    hubDmFriendsList,
    ingestGlobalLobbyChatServerRow,
    ingestFriendDmServerRow,
    appendGlobalLobbyMessageLocal,
    handleLobbyFriendRequestDelivery,
    handleLobbyFriendRequestResolved,
    sendFriendRequestToProfileUser,
    emitFriendRequestResolvedToServer,
    removeAcceptedFriendByPeerId,
    refetchFriendRequestsFromApi,
    refetchFriendPresence,
    friendPresenceByUserId,
    syncHubAfterReconnect,
  };
}
